import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ApiError, post, statusApi } from '../api'
import { t } from '../i18n'
import { useSession } from '../session'
import { Button, Field, Notice } from '../components/ui'
import { BrandLogo } from '../components/Brand'

export function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <p className="mb-6 flex">
          <BrandLogo state="static" />
        </p>
        <div className="sx-panel !p-6">
          <h1 className="mt-0 mb-5 font-sans text-2xl font-semibold tracking-[-.01em]">{title}</h1>
          {children}
        </div>
        <AuthFooter />
      </div>
    </div>
  )
}

/** Under the sign-in card: the status page, when the center has one (STATUS_URL; docs/status-page.md §4). */
function AuthFooter() {
  const q = useQuery({ queryKey: ['status-page-link'], queryFn: statusApi.pageLink, staleTime: 5 * 60_000, retry: false })
  const url = q.data?.page_url
  if (!url) return null
  return (
    <footer className="mt-4 flex justify-center text-xs text-muted" data-testid="auth-footer">
      <a href={url} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:text-text hover:underline" data-testid="auth-status-link">
        {t('auth.footer.status')}
      </a>
    </footer>
  )
}

export function Login() {
  const { refresh } = useSession()
  const nav = useNavigate()
  const loc = useLocation()
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    setBusy(true)
    setErr('')
    try {
      await post('/api/auth/login', { email: f.get('email'), password: f.get('password') })
      await refresh()
      nav((loc.state as { from?: string } | null)?.from ?? nextPath(loc.search) ?? '/', { replace: true })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Shell title="Sign in">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" name="email" type="email" autoComplete="email" required />
        <Field label="Password" name="password" type="password" autoComplete="current-password" required />
        <Notice>{err}</Notice>
        <Button variant="primary" type="submit" disabled={busy} aria-busy={busy || undefined} className="w-full">
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      <p className="mt-5 text-sm text-muted">
        Have an invite code? {/* A full navigation, not a client route: on the admin name the web server sends
            /register to the cabinet (deploy/prod/Caddyfile), where accounts are created. */}
        <Link reloadDocument className="text-accent underline-offset-4 hover:underline" to="/register">
          Create an account
        </Link>
      </p>
    </Shell>
  )
}

export function Register() {
  const { refresh } = useSession()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    setBusy(true)
    setErr('')
    try {
      await post('/api/auth/register', { email: f.get('email'), password: f.get('password'), invite: f.get('invite') })
      await refresh()
      nav('/', { replace: true })
    } catch (e) {
      // 404: this name has no registration (the admin name); say where it is, not "not found".
      setErr(e instanceof ApiError && e.status === 404 ? t('auth.register.elsewhere') : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Shell title="Create an account">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Invite code" name="invite" required defaultValue={params.get('invite') ?? ''} hint="The test stand is invite-only." />
        <Field label="Email" name="email" type="email" autoComplete="email" required />
        <Field label="Password" name="password" type="password" autoComplete="new-password" minLength={10} required hint="At least 10 characters." />
        <Notice>{err}</Notice>
        <Button variant="primary" type="submit" disabled={busy} aria-busy={busy || undefined} className="w-full">
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
      <p className="mt-5 text-sm text-muted">
        Already registered? <Link className="text-accent underline-offset-4 hover:underline" to="/login">Sign in</Link>
      </p>
    </Shell>
  )
}

/**
 * ?next=<path> from the admin name's gate (deploy/prod/Caddyfile): a page of
 * this site only — a path, never another origin (//host, /\host, scheme).
 */
export function nextPath(search: string): string | undefined {
  const n = new URLSearchParams(search).get('next')
  if (!n || !n.startsWith('/') || n.startsWith('//') || n.startsWith('/\\')) return undefined
  return n
}
